import { ATTRIBUTE_FILTER_COPY } from './constants';
import {
    AttributeActiveChip,
    AttributeFiltersSelection,
    AttributeRangeSelection,
    AttributeValueSelection,
    BuildAttributeFilterParamsInput,
    FormatOptionLabelInput
} from './models';

import { CategoryFilterAttribute, CategoryFilterOptionValue } from '@/server/services/core/category/models';

export const createEmptyAttributeFiltersSelection = (): AttributeFiltersSelection => ({
    values: {},
    ranges: {}
});

export const optionValueToString = (value: CategoryFilterOptionValue): string => String(value);

export const getOptionSelectionKey = (value: CategoryFilterOptionValue, unitCode?: string | null): string => {
    const normalizedUnit = unitCode ?? '';
    return `${optionValueToString(value)}::${normalizedUnit}`;
};

export const formatAttributeOptionLabel = ({ value, unitCode, unitName, type }: FormatOptionLabelInput): string => {
    if (type === 'BOOLEAN') {
        return value === true || value === 'true' ? ATTRIBUTE_FILTER_COPY.booleanTrue : ATTRIBUTE_FILTER_COPY.booleanFalse;
    }

    const base = optionValueToString(value);
    const unitLabel = unitName || unitCode;

    return unitLabel ? `${base} ${unitLabel}` : base;
};

const buildEqualsParam = (slug: string, type: CategoryFilterAttribute['type'], selection: AttributeValueSelection): string => {
    if (type === 'NUMBER' && selection.unitCode) {
        return `${slug}==${selection.value}==${selection.unitCode}`;
    }

    return `${slug}==${selection.value}`;
};

export const buildAttributeFilterParams = ({ schema, selection }: BuildAttributeFilterParamsInput): string[] => {
    const schemaBySlug = new Map(schema.map((item) => [item.slug, item]));
    const params: string[] = [];

    Object.entries(selection.values).forEach(([slug, selectedValues]) => {
        const attribute = schemaBySlug.get(slug);
        if (!attribute || attribute.filterMode !== 'VALUES' || !selectedValues.length) {
            return;
        }

        selectedValues.forEach((selectedValue) => {
            params.push(buildEqualsParam(slug, attribute.type, selectedValue));
        });
    });

    Object.entries(selection.ranges).forEach(([slug, range]) => {
        const attribute = schemaBySlug.get(slug);
        if (!attribute || attribute.filterMode !== 'RANGE') {
            return;
        }

        if (range.after) {
            params.push(`${slug}==after==${range.after}`);
        }

        if (range.before) {
            params.push(`${slug}==before==${range.before}`);
        }
    });

    return params;
};

const normalizeFilterParam = (raw: string): string => {
    if (!/%[0-9a-f]{2}/i.test(raw)) {
        return raw;
    }

    try {
        return decodeURIComponent(raw);
    } catch {
        return raw;
    }
};

const parseEqualsParts = (raw: string): { slug: string; rest: string[] } | null => {
    const parts = normalizeFilterParam(raw).split('==');
    if (parts.length < 2) {
        return null;
    }

    const [slug, ...rest] = parts;
    if (!slug || !rest.length) {
        return null;
    }

    return { slug, rest };
};

/**
 * URL for non-NUMBER types omits unitCode (`slug==value`), while schema options may still have unitCode.
 * Resolve the concrete option so UI selection keys match after refresh.
 */
export const resolveAttributeSelectionValue = (
    attribute: CategoryFilterAttribute,
    value: CategoryFilterOptionValue,
    unitCode?: string | null
): AttributeValueSelection | null => {
    const normalizedValue = optionValueToString(value);
    const normalizedUnit = unitCode ?? '';
    const options = attribute.options ?? [];

    const exactMatch = options.find((option) => optionValueToString(option.value) === normalizedValue && (option.unitCode ?? '') === normalizedUnit);

    if (exactMatch) {
        return {
            value: optionValueToString(exactMatch.value),
            unitCode: exactMatch.unitCode ?? null
        };
    }

    if (!normalizedUnit) {
        const valueMatches = options.filter((option) => optionValueToString(option.value) === normalizedValue);

        if (valueMatches.length === 1) {
            return {
                value: optionValueToString(valueMatches[0].value),
                unitCode: valueMatches[0].unitCode ?? null
            };
        }

        const nullUnitMatch = valueMatches.find((option) => option.unitCode == null || option.unitCode === '');
        if (nullUnitMatch) {
            return {
                value: optionValueToString(nullUnitMatch.value),
                unitCode: nullUnitMatch.unitCode ?? null
            };
        }

        if (valueMatches.length > 0) {
            return {
                value: optionValueToString(valueMatches[0].value),
                unitCode: valueMatches[0].unitCode ?? null
            };
        }
    }

    if (attribute.type === 'BOOLEAN') {
        const boolMatch = options.find((option) => {
            const optionIsTrue = option.value === true || option.value === 'true';
            const selectionIsTrue = normalizedValue === 'true';
            return optionIsTrue === selectionIsTrue;
        });

        if (boolMatch) {
            return {
                value: optionValueToString(boolMatch.value),
                unitCode: boolMatch.unitCode ?? null
            };
        }
    }

    if (!options.length) {
        return { value: normalizedValue, unitCode: unitCode ?? null };
    }

    return null;
};

export const parseAttributeFilterParams = (params: string[], schema: CategoryFilterAttribute[] = []): AttributeFiltersSelection => {
    const selection = createEmptyAttributeFiltersSelection();
    const schemaBySlug = new Map(schema.map((item) => [item.slug, item]));

    params.forEach((param) => {
        const parsed = parseEqualsParts(param);
        if (!parsed) {
            return;
        }

        const { slug, rest } = parsed;
        const attribute = schemaBySlug.get(slug);

        if (rest[0] === 'after' || rest[0] === 'before') {
            if (attribute && attribute.filterMode !== 'RANGE') {
                return;
            }

            const dateValue = rest[1];
            if (!dateValue) {
                return;
            }

            const currentRange: AttributeRangeSelection = selection.ranges[slug] ?? {};
            selection.ranges[slug] = {
                ...currentRange,
                [rest[0]]: dateValue
            };
            return;
        }

        if (attribute && attribute.filterMode !== 'VALUES') {
            return;
        }

        const value = rest[0];
        const unitCode = rest[1] ?? null;
        if (value == null || value === '') {
            return;
        }

        const nextValue: AttributeValueSelection = { value, unitCode };
        const currentValues = selection.values[slug] ?? [];
        const exists = currentValues.some((item) => getOptionSelectionKey(item.value, item.unitCode) === getOptionSelectionKey(value, unitCode));

        if (!exists) {
            selection.values[slug] = [...currentValues, nextValue];
        }
    });

    return selection;
};

export const countActiveAttributeFilters = (selection: AttributeFiltersSelection): number => {
    const valuesCount = Object.values(selection.values).reduce((total, items) => total + items.length, 0);
    const rangesCount = Object.values(selection.ranges).reduce((total, range) => {
        return total + (range.after ? 1 : 0) + (range.before ? 1 : 0);
    }, 0);

    return valuesCount + rangesCount;
};

export const isAttributeValueSelected = (
    selection: AttributeFiltersSelection,
    slug: string,
    value: CategoryFilterOptionValue,
    unitCode?: string | null
): boolean => {
    const selected = selection.values[slug] ?? [];
    const key = getOptionSelectionKey(value, unitCode);
    return selected.some((item) => getOptionSelectionKey(item.value, item.unitCode) === key);
};

export const toggleAttributeValueSelection = (
    selection: AttributeFiltersSelection,
    slug: string,
    value: CategoryFilterOptionValue,
    unitCode?: string | null
): AttributeFiltersSelection => {
    const currentValues = selection.values[slug] ?? [];
    const key = getOptionSelectionKey(value, unitCode);
    const exists = currentValues.some((item) => getOptionSelectionKey(item.value, item.unitCode) === key);

    const nextValues = exists
        ? currentValues.filter((item) => getOptionSelectionKey(item.value, item.unitCode) !== key)
        : [...currentValues, { value: optionValueToString(value), unitCode }];

    const nextSelectionValues = { ...selection.values };

    if (nextValues.length) {
        nextSelectionValues[slug] = nextValues;
    } else {
        delete nextSelectionValues[slug];
    }

    return {
        ...selection,
        values: nextSelectionValues
    };
};

export const setAttributeValuesSelection = (
    selection: AttributeFiltersSelection,
    slug: string,
    values: AttributeValueSelection[]
): AttributeFiltersSelection => {
    const nextSelectionValues = { ...selection.values };

    if (values.length) {
        nextSelectionValues[slug] = values.map((item) => ({
            value: optionValueToString(item.value),
            unitCode: item.unitCode ?? null
        }));
    } else {
        delete nextSelectionValues[slug];
    }

    return {
        ...selection,
        values: nextSelectionValues
    };
};

export const setAttributeRangeSelection = (
    selection: AttributeFiltersSelection,
    slug: string,
    key: keyof AttributeRangeSelection,
    date: string | undefined
): AttributeFiltersSelection => {
    const currentRange = selection.ranges[slug] ?? {};
    const nextRange: AttributeRangeSelection = {
        ...currentRange,
        [key]: date || undefined
    };

    const nextRanges = { ...selection.ranges };

    if (!nextRange.after && !nextRange.before) {
        delete nextRanges[slug];
    } else {
        nextRanges[slug] = nextRange;
    }

    return {
        ...selection,
        ranges: nextRanges
    };
};

export const removeAttributeActiveChip = (selection: AttributeFiltersSelection, chip: AttributeActiveChip): AttributeFiltersSelection => {
    if (chip.kind === 'value') {
        const currentValues = selection.values[chip.slug] ?? [];
        const nextValues = currentValues.filter(
            (item) => getOptionSelectionKey(item.value, item.unitCode) !== getOptionSelectionKey(chip.value ?? '', chip.unitCode)
        );
        const nextSelectionValues = { ...selection.values };

        if (nextValues.length) {
            nextSelectionValues[chip.slug] = nextValues;
        } else {
            delete nextSelectionValues[chip.slug];
        }

        return {
            ...selection,
            values: nextSelectionValues
        };
    }

    return setAttributeRangeSelection(selection, chip.slug, chip.kind, undefined);
};

export const isAttributeRangeValid = (range?: AttributeRangeSelection): boolean => {
    if (!range?.after || !range?.before) {
        return true;
    }

    return range.after <= range.before;
};

export const hasInvalidAttributeRanges = (selection: AttributeFiltersSelection): boolean => {
    return Object.values(selection.ranges).some((range) => !isAttributeRangeValid(range));
};

export const buildAttributeActiveChips = (schema: CategoryFilterAttribute[], selection: AttributeFiltersSelection): AttributeActiveChip[] => {
    const chips: AttributeActiveChip[] = [];
    const schemaBySlug = new Map(schema.map((item) => [item.slug, item]));

    Object.entries(selection.values).forEach(([slug, values]) => {
        const attribute = schemaBySlug.get(slug);
        if (!attribute) {
            return;
        }

        values.forEach((item) => {
            const matchedOption = attribute.options.find(
                (option) => getOptionSelectionKey(option.value, option.unitCode) === getOptionSelectionKey(item.value, item.unitCode)
            );

            chips.push({
                id: `${slug}-value-${getOptionSelectionKey(item.value, item.unitCode)}`,
                slug,
                attributeName: attribute.name,
                label:
                    attribute.type === 'BOOLEAN'
                        ? attribute.name
                        : `${attribute.name}: ${formatAttributeOptionLabel({
                              value: item.value,
                              unitCode: item.unitCode,
                              unitName: matchedOption?.unitName,
                              type: attribute.type
                          })}`,
                kind: 'value',
                value: item.value,
                unitCode: item.unitCode
            });
        });
    });

    Object.entries(selection.ranges).forEach(([slug, range]) => {
        const attribute = schemaBySlug.get(slug);
        if (!attribute) {
            return;
        }

        if (range.after) {
            chips.push({
                id: `${slug}-after`,
                slug,
                attributeName: attribute.name,
                label: `${attribute.name}: از ${range.after}`,
                kind: 'after'
            });
        }

        if (range.before) {
            chips.push({
                id: `${slug}-before`,
                slug,
                attributeName: attribute.name,
                label: `${attribute.name}: تا ${range.before}`,
                kind: 'before'
            });
        }
    });

    return chips;
};

export const pruneAttributeFiltersSelection = (
    selection: AttributeFiltersSelection,
    schema: CategoryFilterAttribute[]
): AttributeFiltersSelection => {
    const schemaBySlug = new Map(schema.map((item) => [item.slug, item]));
    const nextValues: AttributeFiltersSelection['values'] = {};
    const nextRanges: AttributeFiltersSelection['ranges'] = {};

    Object.entries(selection.values).forEach(([slug, values]) => {
        const attribute = schemaBySlug.get(slug);
        if (!attribute || attribute.filterMode !== 'VALUES' || !values.length) {
            return;
        }

        const resolvedValues = values
            .map((item) => resolveAttributeSelectionValue(attribute, item.value, item.unitCode))
            .filter((item): item is AttributeValueSelection => Boolean(item));

        if (!resolvedValues.length) {
            return;
        }

        const deduped: AttributeValueSelection[] = [];
        resolvedValues.forEach((item) => {
            const key = getOptionSelectionKey(item.value, item.unitCode);
            if (!deduped.some((existing) => getOptionSelectionKey(existing.value, existing.unitCode) === key)) {
                deduped.push(item);
            }
        });

        nextValues[slug] = deduped;
    });

    Object.entries(selection.ranges).forEach(([slug, range]) => {
        const attribute = schemaBySlug.get(slug);
        if (!attribute || attribute.filterMode !== 'RANGE') {
            return;
        }

        if (range.after || range.before) {
            nextRanges[slug] = range;
        }
    });

    return {
        values: nextValues,
        ranges: nextRanges
    };
};
